import { useEffect, useState, useCallback } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  ExternalLink,
  Rocket,
  Trash2,
  RefreshCw,
  Eye,
  Palette,
  Loader2,
  AlertCircle,
  Copy,
  Check,
  Save,
  Layers,
} from 'lucide-react'
import { api, type SiteContent, type DesignConfig } from '@/lib/api'
import { ContentEditor } from '@/components/editor'
import {
  formatDate,
  formatNumber,
  getStatusColor,
  getStrategyLabel,
  copyToClipboard,
  cn,
} from '@/lib/utils'
import toast from 'react-hot-toast'

const FONT_OPTIONS = [
  'Inter',
  'DM Sans',
  'Manrope',
  'Space Grotesk',
  'Outfit',
  'Montserrat',
  'Merriweather',
  'Playfair Display',
  'Libre Baskerville',
  'Poppins',
  'Nunito',
  'Source Sans Pro',
]

const AESTHETIC_OPTIONS = [
  'modern',
  'minimal',
  'bold',
  'elegant',
  'playful',
  'editorial',
  'luxury',
]

const LAYOUT_OPTIONS = [
  'centered',
  'wide',
  'asymmetric',
  'grid',
  'magazine',
  'hero-focused',
]

export function SiteDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<'preview' | 'content' | 'design' | 'templates'>('preview')
  const [copied, setCopied] = useState(false)
  const [contentDraft, setContentDraft] = useState<SiteContent | null>(null)
  const [designDraft, setDesignDraft] = useState<DesignConfig | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<string>('')

  const { data, isLoading, error } = useQuery({
    queryKey: ['site', id],
    queryFn: () => api.getSite(id!),
    enabled: !!id,
  })

  const { data: previewHtml, isLoading: previewLoading, refetch: refetchPreview } = useQuery({
    queryKey: ['site-preview', id],
    queryFn: () => api.getSitePreview(id!),
    enabled: !!id,
  })

  const { data: templatesData, isLoading: templatesLoading } = useQuery({
    queryKey: ['templates', data?.site?.site_type, selectedCategory],
    queryFn: () => api.getTemplates(data?.site?.site_type || undefined, selectedCategory || undefined),
    enabled: activeTab === 'templates',
  })

  const { data: templateCategories } = useQuery({
    queryKey: ['template-categories'],
    queryFn: () => api.getTemplateCategories(),
    enabled: activeTab === 'templates',
  })

  const deployMutation = useMutation({
    mutationFn: () => api.deploySite(id!),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['site', id] })
      toast.success(`Site deployed! ${data.url}`)
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })

  const regenerateMutation = useMutation({
    mutationFn: (type: 'content' | 'design' | 'all') =>
      api.regenerateSite(id!, type),
    onSuccess: (data) => {
      if (data.content) {
        setContentDraft(data.content)
      }
      if (data.design) {
        setDesignDraft(data.design)
      }
      queryClient.invalidateQueries({ queryKey: ['site', id] })
      queryClient.invalidateQueries({ queryKey: ['site-preview', id] })
      toast.success('Regenerated!')
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })

  const updateSiteMutation = useMutation({
    mutationFn: (payload: { content?: SiteContent; design?: DesignConfig }) =>
      api.updateSite(id!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site', id] })
      queryClient.invalidateQueries({ queryKey: ['site-preview', id] })
      refetchPreview()
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => api.deleteSite(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites'] })
      toast.success('Site deleted')
      navigate('/dashboard/sites')
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })

  const applyTemplateMutation = useMutation({
    mutationFn: async (templateId: string) => {
      const template = await api.getTemplate(templateId)
      return api.updateSite(id!, { design: template.config })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site', id] })
      queryClient.invalidateQueries({ queryKey: ['site-preview', id] })
      toast.success('Template applied!')
      setActiveTab('preview')
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })

  const handleCopyUrl = async () => {
    if (data?.site?.deployed_url) {
      await copyToClipboard(data.site.deployed_url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
      toast.success('URL copied!')
    }
  }

  const handleSaveContent = useCallback(() => {
    if (contentDraft) {
      updateSiteMutation.mutate({ content: contentDraft })
    }
  }, [contentDraft, updateSiteMutation])

  const handleRegenerate = useCallback(
    async (type: 'content' | 'section', sectionIndex?: number) => {
      if (type === 'content') {
        regenerateMutation.mutate('content')
      } else if (type === 'section' && sectionIndex !== undefined) {
        // TODO: Implement per-section regeneration API
        toast.success('Section regeneration coming soon!')
      }
    },
    [regenerateMutation]
  )

  const site = data?.site

  useEffect(() => {
    if (site?.content) {
      setContentDraft(site.content)
    }
    if (site?.design) {
      setDesignDraft(site.design)
    }
  }, [site?.id])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  if (error || !site) {
    return (
      <div className="text-center py-20">
        <AlertCircle className="h-12 w-12 mx-auto text-destructive mb-4" />
        <h2 className="text-xl font-semibold mb-2">Site not found</h2>
        <Link to="/dashboard/sites" className="btn-primary">
          Back to Sites
        </Link>
      </div>
    )
  }

  const colorEntries = designDraft
    ? (Object.entries(designDraft.colorScheme) as Array<
        [keyof DesignConfig['colorScheme'], string]
      >)
    : []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <Link to="/dashboard/sites" className="btn-ghost p-2 self-start">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-display font-bold">{site.domain_name}</h1>
            <span className={cn('badge', `badge-${getStatusColor(site.status)}`)}>
              {site.status}
            </span>
          </div>
          <p className="text-muted-foreground">
            {getStrategyLabel(site.site_type)} • Created {formatDate(site.created_at)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {site.status === 'draft' ? (
            <button
              onClick={() => deployMutation.mutate()}
              disabled={deployMutation.isPending}
              className="btn-primary"
            >
              {deployMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Deploying...
                </>
              ) : (
                <>
                  <Rocket className="h-4 w-4" />
                  Deploy
                </>
              )}
            </button>
          ) : site.deployed_url ? (
            <a
              href={site.deployed_url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
            >
              <ExternalLink className="h-4 w-4" />
              Visit Site
            </a>
          ) : null}

          <button
            onClick={() => {
              if (confirm('Are you sure you want to delete this site?')) {
                deleteMutation.mutate()
              }
            }}
            className="btn-outline text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Deployed URL */}
      {site.deployed_url && (
        <div className="card bg-success/5 border-success/20">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Live at</p>
              <a
                href={site.deployed_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline font-medium"
              >
                {site.deployed_url}
              </a>
            </div>
            <button onClick={handleCopyUrl} className="btn-ghost btn-sm">
              {copied ? (
                <Check className="h-4 w-4 text-success" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="card">
          <p className="text-sm text-muted-foreground">Total Visits</p>
          <p className="text-2xl font-display font-bold">
            {formatNumber(site.total_visits)}
          </p>
        </div>
        <div className="card">
          <p className="text-sm text-muted-foreground">Total Leads</p>
          <p className="text-2xl font-display font-bold">
            {formatNumber(site.total_leads)}
          </p>
        </div>
        <div className="card">
          <p className="text-sm text-muted-foreground">Conversion Rate</p>
          <p className="text-2xl font-display font-bold">
            {site.total_visits > 0
              ? ((site.total_leads / site.total_visits) * 100).toFixed(1)
              : 0}
            %
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-border">
        <nav className="flex gap-4 overflow-x-auto">
          {[
            { id: 'preview', label: 'Preview', icon: Eye },
            { id: 'content', label: 'Content', icon: Palette },
            { id: 'design', label: 'Design', icon: Palette },
            { id: 'templates', label: 'Templates', icon: Layers },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={cn(
                'flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap',
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              )}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'preview' && (
          <div className="card p-0 overflow-hidden">
            {/* Browser Chrome */}
            <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-muted/50">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-destructive/50" />
                <div className="w-3 h-3 rounded-full bg-accent/50" />
                <div className="w-3 h-3 rounded-full bg-success/50" />
              </div>
              <div className="flex-1 flex justify-center">
                <div className="px-4 py-1 rounded-md bg-background text-xs text-muted-foreground">
                  {site.domain_name}
                </div>
              </div>
              <button
                onClick={() => regenerateMutation.mutate('all')}
                disabled={regenerateMutation.isPending}
                className="btn-ghost btn-sm"
              >
                {regenerateMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
              </button>
            </div>

            {/* Preview Frame */}
            {previewLoading ? (
              <div className="aspect-video flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : previewHtml ? (
              <iframe
                srcDoc={previewHtml}
                className="w-full aspect-video border-0"
                title="Site Preview"
              />
            ) : (
              <div className="aspect-video flex items-center justify-center text-muted-foreground">
                Preview not available
              </div>
            )}
          </div>
        )}

        {activeTab === 'content' && contentDraft && (
          <ContentEditor
            content={contentDraft}
            onChange={setContentDraft}
            onSave={handleSaveContent}
            onRegenerate={handleRegenerate}
            isSaving={updateSiteMutation.isPending}
            isRegenerating={regenerateMutation.isPending}
            previewHtml={previewHtml}
            previewLoading={previewLoading}
            siteId={id}
          />
        )}

        {activeTab === 'design' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-sm text-muted-foreground">
                Adjust colors and typography to match your brand.
              </p>
              <button
                onClick={() => regenerateMutation.mutate('design')}
                disabled={regenerateMutation.isPending}
                className="btn-outline btn-sm"
              >
                {regenerateMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Regenerating...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4" />
                    Regenerate Design
                  </>
                )}
              </button>
            </div>

            {designDraft ? (
              <div className="card space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  {colorEntries.map(([name, color]) => (
                    <div key={name}>
                      <label className="label mb-1.5 block capitalize">{name} Color</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={color}
                          onChange={(e) =>
                            setDesignDraft({
                              ...designDraft,
                              colorScheme: {
                                ...designDraft.colorScheme,
                                [name]: e.target.value,
                              },
                            })
                          }
                          className="h-10 w-12 rounded border border-border bg-transparent"
                        />
                        <input
                          type="text"
                          value={color}
                          onChange={(e) =>
                            setDesignDraft({
                              ...designDraft,
                              colorScheme: {
                                ...designDraft.colorScheme,
                                [name]: e.target.value,
                              },
                            })
                          }
                          className="input flex-1"
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="label mb-1.5 block">Heading Font</label>
                    <select
                      value={designDraft.typography.headingFont}
                      onChange={(e) =>
                        setDesignDraft({
                          ...designDraft,
                          typography: {
                            ...designDraft.typography,
                            headingFont: e.target.value,
                          },
                        })
                      }
                      className="input"
                    >
                      {FONT_OPTIONS.map((font) => (
                        <option key={font} value={font}>
                          {font}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="label mb-1.5 block">Body Font</label>
                    <select
                      value={designDraft.typography.bodyFont}
                      onChange={(e) =>
                        setDesignDraft({
                          ...designDraft,
                          typography: {
                            ...designDraft.typography,
                            bodyFont: e.target.value,
                          },
                        })
                      }
                      className="input"
                    >
                      {FONT_OPTIONS.map((font) => (
                        <option key={font} value={font}>
                          {font}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="label mb-1.5 block">Aesthetic</label>
                    <select
                      value={designDraft.aesthetic}
                      onChange={(e) =>
                        setDesignDraft({ ...designDraft, aesthetic: e.target.value })
                      }
                      className="input"
                    >
                      {AESTHETIC_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="label mb-1.5 block">Layout</label>
                    <select
                      value={designDraft.layout}
                      onChange={(e) =>
                        setDesignDraft({ ...designDraft, layout: e.target.value })
                      }
                      className="input"
                    >
                      {LAYOUT_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 justify-end">
                  <button
                    onClick={() => setDesignDraft(site.design)}
                    className="btn-ghost btn-sm"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Reset
                  </button>
                  <button
                    onClick={() =>
                      updateSiteMutation.mutate({ design: designDraft })
                    }
                    disabled={updateSiteMutation.isPending}
                    className="btn-primary btn-sm"
                  >
                    {updateSiteMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4" />
                        Save Design
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <div className="card text-center py-12 text-muted-foreground">
                No design generated yet
              </div>
            )}

            {site.design && (
              <div className="grid gap-4 sm:grid-cols-2">
                {/* Color Scheme */}
                <div className="card">
                  <h3 className="font-semibold mb-4">Color Scheme</h3>
                  <div className="space-y-3">
                    {Object.entries(site.design.colorScheme).map(([name, color]) => (
                      <div key={name} className="flex items-center gap-3">
                        <div
                          className="w-8 h-8 rounded-lg border border-border"
                          style={{ backgroundColor: color }}
                        />
                        <div>
                          <p className="text-sm font-medium capitalize">{name}</p>
                          <p className="text-xs text-muted-foreground">{color}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Typography */}
                <div className="card">
                  <h3 className="font-semibold mb-4">Typography</h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-muted-foreground">Heading Font</p>
                      <p
                        className="text-lg font-semibold"
                        style={{ fontFamily: site.design.typography.headingFont }}
                      >
                        {site.design.typography.headingFont}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Body Font</p>
                      <p style={{ fontFamily: site.design.typography.bodyFont }}>
                        {site.design.typography.bodyFont}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Style */}
                <div className="card">
                  <h3 className="font-semibold mb-4">Style</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Aesthetic</span>
                      <span className="capitalize">{site.design.aesthetic}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Layout</span>
                      <span className="capitalize">{site.design.layout}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'templates' && (
          <div className="space-y-6">
            <div>
              <p className="text-muted-foreground mb-4">
                Choose a pre-designed template to instantly transform your site's look and feel.
              </p>

              {/* Category Filter */}
              <div className="flex flex-wrap gap-2 mb-6">
                <button
                  onClick={() => setSelectedCategory('')}
                  className={cn(
                    'px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
                    !selectedCategory
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted hover:bg-muted/80'
                  )}
                >
                  All
                </button>
                {templateCategories?.map((cat) => (
                  <button
                    key={cat.category}
                    onClick={() => setSelectedCategory(cat.category)}
                    className={cn(
                      'px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
                      selectedCategory === cat.category
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted hover:bg-muted/80'
                    )}
                  >
                    {cat.label} ({cat.count})
                  </button>
                ))}
              </div>
            </div>

            {/* Templates Grid */}
            {templatesLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : templatesData?.templates?.length === 0 ? (
              <div className="card text-center py-12">
                <p className="text-muted-foreground">No templates found for this category.</p>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {templatesData?.templates?.map((template) => (
                  <div
                    key={template.id}
                    className={cn(
                      'card group cursor-pointer transition-all hover:border-primary',
                      template.isDark ? 'bg-foreground/5' : ''
                    )}
                    onClick={() => applyTemplateMutation.mutate(template.id)}
                  >
                    {/* Template Preview */}
                    <div
                      className="aspect-video rounded-lg mb-3 relative overflow-hidden"
                      style={{
                        background: template.isDark
                          ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
                          : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                      }}
                    >
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div
                          className={cn(
                            'text-center px-4',
                            template.isDark ? 'text-white' : 'text-gray-800'
                          )}
                        >
                          <p className="font-semibold text-sm mb-1">{template.name}</p>
                          <p className="text-xs opacity-70 capitalize">{template.aesthetic}</p>
                        </div>
                      </div>
                      <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/10 transition-colors flex items-center justify-center">
                        <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-primary text-primary-foreground px-3 py-1.5 rounded-full text-sm font-medium">
                          Apply Template
                        </span>
                      </div>
                    </div>

                    {/* Template Info */}
                    <h3 className="font-semibold mb-1">{template.name}</h3>
                    <p className="text-sm text-muted-foreground mb-2">{template.description}</p>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-1">
                      <span className="badge badge-muted text-xs capitalize">{template.category}</span>
                      <span className="badge badge-muted text-xs capitalize">{template.layout}</span>
                      {template.isDark && (
                        <span className="badge badge-muted text-xs">Dark</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {applyTemplateMutation.isPending && (
              <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50">
                <div className="card text-center">
                  <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-3" />
                  <p className="font-medium">Applying template...</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
